import {
  CheckIcon,
  ComboboxItem,
  ComboboxParsedItem,
  Input,
  Loader,
  Checkbox as MantineCheckbox,
  MultiSelect as MantineMultiSelect,
  Radio as MantineRadio,
  Select as MantineSelect,
  Stack,
  Text,
  TextInput,
  Textarea,
} from "@mantine/core";
import { AnyFieldApi, ValidationError } from "@tanstack/react-form";
import { type JSX, ReactNode, useMemo, useRef, useState } from "react";

import SvgIcon from "~/components/SvgIcon";
import { multiSelectSurfaceStyles } from "~/utils/Theme";
import {
  FormInputItem,
  LabelValuePair,
  LooseObject,
  MessageText,
} from "~/utils/Types";

export const SELECT_ALL_VALUE = "select_all";

/** Extract a display string from a ValidationError (string or StandardSchemaV1Issue). */
const errorToString = (error: ValidationError): string =>
  typeof error === "string"
    ? error
    : error && typeof error === "object" && "message" in error
      ? String(error.message)
      : String(error);

const getErrorMessage = (errors?: ValidationError[]): string | undefined => {
  if (!errors?.length) return undefined;
  return [...new Set(errors.map(errorToString))].join(", ");
};

/**
 * MUI-era `inputProps`/`textFieldProps` passthrough, mapped to the Mantine
 * equivalents the call sites actually use (start/end adornments, native input
 * attributes like maxLength/autoComplete).
 */
const splitMuiInputProps = (raw?: object) => {
  const {
    startAdornment,
    endAdornment,
    notched: _notched,
    ...nativeProps
  } = (raw ?? {}) as LooseObject;
  return {
    leftSection: startAdornment as ReactNode | undefined,
    rightSection: endAdornment as ReactNode | undefined,
    nativeProps,
  };
};

type FieldRendererProps = {
  item: FormInputItem;
  field: AnyFieldApi;
  style?: React.CSSProperties;
};

/**
 * Label-above + error/helper-below shell, matching the legacy MUI
 * FormControl/InputLabel/FormHelperText composition (label was always
 * "shrunk" above the input; errors show once the field is touched).
 */
const FieldShell = ({
  item,
  field,
  children,
  label,
  description,
}: FieldRendererProps & {
  children: JSX.Element;
  label?: ReactNode;
  description?: ReactNode;
}) => {
  const showError =
    !!field.state.meta.errors.length && field.state.meta.isTouched;
  const errorMessage = showError
    ? getErrorMessage(field.state.meta.errors)
    : undefined;
  // A required field's label already carries the red "Required" suffix while
  // it is empty, so repeating the plain required message under the input
  // would show "Required" twice; the input's own red border still flags it.
  const displayError =
    item.required && errorMessage === MessageText.INPUT_ERROR_REQUIRED
      ? undefined
      : errorMessage;
  return (
    <Input.Wrapper
      w="100%"
      // `label` may be an explicit null to suppress the shell label (the
      // checkbox renders item.label itself); `??` would resurrect item.label.
      label={label !== undefined ? label : item.label}
      error={displayError}
      // Like `label`, an explicit null suppresses the shell helper (the
      // checkbox shows item.helperText in its own label-aligned slot).
      description={
        displayError
          ? undefined
          : description !== undefined
            ? description
            : item.helperText
      }
      inputWrapperOrder={["label", "input", "description", "error"]}
      styles={{
        label: { marginBottom: 5 },
        description: { marginTop: 8 },
        error: { marginTop: 8 },
      }}
    >
      {children}
    </Input.Wrapper>
  );
};

const hasError = (field: AnyFieldApi) =>
  !!field.state.meta.errors.length && field.state.meta.isTouched;

export const FormTextField = ({ item, field, style }: FieldRendererProps) => {
  const { leftSection, rightSection, nativeProps } = splitMuiInputProps(
    item.inputProps,
  );
  const shared = {
    w: "100%",
    placeholder: item.placeholder,
    name: item.name,
    value: (field.state.value as string) ?? "",
    onBlur: field.handleBlur,
    error: hasError(field),
    disabled: item.disabled,
    size: item.size === "small" ? "sm" : "md",
    style,
  } as const;

  return (
    <FieldShell item={item} field={field}>
      {item.multiline ? (
        <Textarea
          {...shared}
          minRows={3}
          maxRows={7}
          autosize
          onChange={(e) => {
            field.handleChange(e.currentTarget.value);
            item.exposeValue?.(e.currentTarget.value);
          }}
        />
      ) : (
        <TextInput
          {...shared}
          type={item.type}
          autoComplete="new-password"
          leftSection={leftSection}
          rightSection={rightSection}
          onWheel={(e) => (e.target as HTMLElement).blur()}
          onChange={(e) => {
            field.handleChange(e.currentTarget.value);
            item.exposeValue?.(e.currentTarget.value);
          }}
          {...nativeProps}
        />
      )}
    </FieldShell>
  );
};

/**
 * Single/multi select on Mantine Select/MultiSelect. Option values keep
 * their ORIGINAL types (numbers, etc.) — options are keyed by index
 * internally, same approach as FormField's AutocompleteField. Retains the
 * legacy "Select All" pseudo-option for multi selects.
 */
export const FormSelect = ({ item, field, style }: FieldRendererProps) => {
  const options: LabelValuePair[] = item.options ? [...item.options] : [];
  const { leftSection, rightSection } = splitMuiInputProps(item.inputProps);

  const keyOf = (index: number) => String(index);
  const optionAt = (key: string | null) =>
    key == null ? null : options[Number(key)]?.value;
  // Loose on type: a comma-joined string value (legacy persisted prefs) splits
  // into strings, which must still match numeric option values.
  const keyOfValue = (value: unknown) =>
    keyOf(
      options.findIndex(
        (o) => o.value === value || String(o.value) === String(value),
      ),
    );

  // Mantine's data labels must be plain strings (they're what the input
  // displays and filters on), but LabelValuePair allows ReactNode labels
  // (e.g. the GMP StatusChip options). For those, fall back to the option
  // value for the input text and render the original node in the dropdown.
  const isPrimitiveLabel = (label: ReactNode) =>
    typeof label === "string" || typeof label === "number";
  const data = useMemo(
    () =>
      options.map((o, index) => ({
        value: keyOf(index),
        label: isPrimitiveLabel(o.label)
          ? String(o.label)
          : String(o.value ?? ""),
        disabled: o.disabled,
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- options is rebuilt per render from item.options
    [item.options],
  );

  const renderOption = ({
    option,
    checked,
  }: {
    option: ComboboxItem;
    checked?: boolean;
  }) => {
    const original = options[Number(option.value)];
    const labelNode =
      original &&
      !isPrimitiveLabel(original.label) &&
      original.label != null ? (
        original.label
      ) : (
        <Text size="sm">{option.label}</Text>
      );
    const content = original?.description ? (
      <Stack gap={0}>
        {labelNode}
        <Text size="xs" c="var(--mantine-color-text-tertiary)">
          {original.description}
        </Text>
      </Stack>
    ) : (
      labelNode
    );
    // A custom renderOption replaces Mantine's whole option body, including
    // the check mark on selected options — without re-adding it a multi
    // select gives no dropdown feedback at all. Sized/faded to match
    // Mantine's own optionsDropdownCheckIcon; the option's data-reverse
    // layout (checkIconPosition: "right") pushes it to the far edge.
    return item.multiple ? (
      <>
        {content}
        {checked && (
          <CheckIcon
            style={{
              opacity: 0.4,
              width: "0.8em",
              minWidth: "0.8em",
              height: "0.8em",
            }}
          />
        )}
      </>
    ) : (
      content
    );
  };

  const shared = {
    w: "100%",
    data,
    placeholder: item.placeholder,
    onBlur: field.handleBlur,
    error: hasError(field),
    disabled: item.disabled,
    size: item.size === "small" ? "sm" : "md",
    leftSection,
    rightSection,
    renderOption,
    comboboxProps: { withinPortal: true, ...item.comboboxProps },
    style,
    nothingFoundMessage: "No options available.",
  } as const;

  if (item.multiple) {
    const rawValue = field.state.value;
    const currentValues: unknown[] =
      typeof rawValue === "string"
        ? rawValue === ""
          ? []
          : rawValue.split(",")
        : ((rawValue as unknown[]) ?? []);
    const allKeys = options
      .filter((o) => o.value !== SELECT_ALL_VALUE)
      .map((o) => keyOfValue(o.value));

    const dataWithSelectAll = item.selectAll
      ? [{ value: SELECT_ALL_VALUE, label: "Select All" }, ...data]
      : data;

    return (
      <FieldShell item={item} field={field}>
        <MantineMultiSelect
          {...shared}
          data={dataWithSelectAll}
          // The theme hides MultiSelect pills for filter-style selects; a
          // form select must show what is selected (parity with the legacy
          // MUI Chips), so rebuild the themed surface without the pill
          // override. Values with no matching option (stale data) map to
          // index -1 and would render as broken "-1" pills — drop them.
          styles={multiSelectSurfaceStyles}
          value={currentValues
            .filter((v) => v !== SELECT_ALL_VALUE)
            .map(keyOfValue)
            .filter((k) => Number(k) >= 0)}
          onChange={(keys) => {
            let nextKeys = keys;
            if (item.selectAll && keys.includes(SELECT_ALL_VALUE)) {
              const others = keys.filter((k) => k !== SELECT_ALL_VALUE);
              nextKeys = others.length === allKeys.length ? [] : allKeys;
            }
            const next = nextKeys
              .filter((k) => k !== SELECT_ALL_VALUE)
              .map(optionAt);
            field.handleChange(next);
            item.exposeValue?.(next);
          }}
        />
      </FieldShell>
    );
  }

  const value = field.state.value;
  const selectedKey =
    value === undefined || value === null || value === ""
      ? (item.defaultValue ?? "") === ""
        ? null
        : keyOfValue(item.defaultValue)
      : keyOfValue(value);

  return (
    <FieldShell item={item} field={field}>
      <MantineSelect
        {...shared}
        value={
          selectedKey != null && Number(selectedKey) >= 0 ? selectedKey : null
        }
        onChange={(key) => {
          const next = optionAt(key);
          field.handleChange(next);
          item.exposeValue?.(next);
        }}
        allowDeselect={false}
      />
    </FieldShell>
  );
};

/**
 * MUI-Autocomplete-era behaviour on Mantine Select/MultiSelect: options keep
 * original shapes (objects/strings, keyed by index), custom
 * filter/group/label functions supported, and the freeSolo text-capture
 * behaviours (`autoCompleteShowInputValue` / `autoCompleteExposeInputValue`)
 * are preserved via the searchable input.
 */
export const FormAutocomplete = ({
  item,
  field,
  style,
}: FieldRendererProps) => {
  const [searchValue, setSearchValue] = useState("");
  // Mirror of searchValue plus its previous value, so the blur handler can
  // tell Mantine's blur-time search reset apart from the user clearing the
  // text themselves (see handleBlur).
  const searchRef = useRef("");
  const previousSearchRef = useRef("");
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- option shapes vary per call site, as with the previous MUI Autocomplete
  type Option = any;
  const options: Option[] = useMemo(
    () => [...(item.options ?? [])],
    [item.options],
  );

  const getLabel = (option: Option): string =>
    option == null || option === ""
      ? ""
      : item.getOptionLabel
        ? item.getOptionLabel(option)
        : typeof option === "object" && option !== null && "label" in option
          ? String((option as LooseObject).label)
          : String(option);

  const keyOf = (index: number) => String(index);
  const optionAt = (key: string | null) =>
    key == null ? null : options[Number(key)];

  const data = useMemo(() => {
    const items = options.map((option: Option, index: number) => ({
      value: keyOf(index),
      label: getLabel(option),
    }));
    if (!item.groupBy) return items;
    const groups = new Map<string, typeof items>();
    options.forEach((option: Option, index: number) => {
      const group = item.groupBy!(option) ?? "";
      if (!groups.has(group)) groups.set(group, []);
      groups.get(group)!.push(items[index]);
    });
    return [...groups.entries()].map(([group, groupItems]) => ({
      group,
      items: groupItems,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- getLabel derives from item props
  }, [options, item.groupBy, item.getOptionLabel]);

  const filter = useMemo(() => {
    if (!item.autoCompleteFilterOptions) return undefined;
    return ({
      options: parsed,
      search,
    }: {
      options: ComboboxParsedItem[];
      search: string;
    }): ComboboxParsedItem[] => {
      const kept = new Set(
        item
          .autoCompleteFilterOptions(options, {
            inputValue: search,
            getOptionLabel: getLabel,
          })
          // Match by reference or label, not indexOf: a filter that returns
          // fresh objects would otherwise keep nothing.
          .map((kept: Option) =>
            keyOf(
              options.findIndex(
                (option) =>
                  option === kept || getLabel(option) === getLabel(kept),
              ),
            ),
          ),
      );
      const keepItem = (dataItem: ComboboxItem) => kept.has(dataItem.value);
      return parsed
        .map((dataItem) =>
          "group" in dataItem
            ? { ...dataItem, items: dataItem.items.filter(keepItem) }
            : dataItem,
        )
        .filter((dataItem) =>
          "group" in dataItem
            ? dataItem.items.length > 0
            : keepItem(dataItem as ComboboxItem),
        );
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- getLabel derives from item props
  }, [item.autoCompleteFilterOptions, options, item.getOptionLabel]);

  const renderOptionMantine = item.autoCompleteRenderOption
    ? ({ option }: { option: ComboboxItem }) =>
        item.autoCompleteRenderOption!({}, optionAt(option.value))
    : undefined;

  const handleSearchChange = (value: string) => {
    previousSearchRef.current = searchRef.current;
    searchRef.current = value;
    setSearchValue(value);
    item.autoCompleteExposeInputValue?.(value);
    if (item.autoCompleteShowInputValue) {
      const freeSoloValue = { label: value, value: value || "" };
      field.handleChange(freeSoloValue);
      item.exposeValue?.(freeSoloValue);
    }
  };

  const handleBlur = () => {
    // Mantine's searchable Select resets the search text on blur to the
    // selected option's label — "" when the text was typed rather than picked
    // from the list. For fields that capture typed text — into the form state
    // (autoCompleteShowInputValue, e.g. a hand-entered PIC on the supply
    // agreement) or into caller state (autoCompleteExposeInputValue, e.g. a
    // new GMP business name on the Users forms) — that reset has already run
    // and wiped it by the time this handler fires, so undo it. A user
    // clearing the field themselves empties the search BEFORE blur, in which
    // case the reset is ""→"" and nothing is restored.
    if (
      (item.autoCompleteShowInputValue || item.autoCompleteExposeInputValue) &&
      searchRef.current === "" &&
      previousSearchRef.current !== ""
    ) {
      handleSearchChange(previousSearchRef.current);
    }
    field.handleBlur();
  };

  const currentValue =
    field.state.value ||
    (item.showDefaultValueWhenEmpty
      ? item.defaultValue
      : item.multiple
        ? []
        : "");

  const shared = {
    w: "100%",
    data,
    placeholder: item.placeholder || "Type to search",
    searchable: true,
    clearable: false,
    filter,
    renderOption: renderOptionMantine,
    disabled: item.disabled,
    size: item.size === "small" ? "sm" : "md",
    error: hasError(field),
    onBlur: handleBlur,
    leftSection: item.autoCompleteShowSearchIcon ? (
      <SvgIcon name="SearchSm" strokeWidth={1.6} />
    ) : undefined,
    rightSection: item.loading ? <Loader size="xs" /> : undefined,
    searchValue,
    onSearchChange: handleSearchChange,
    comboboxProps: { withinPortal: true, ...item.comboboxProps },
    style,
  } as const;

  return (
    <FieldShell item={item} field={field}>
      {item.multiple ? (
        <MantineMultiSelect
          {...shared}
          // The theme hides MultiSelect pills for filter-style selects; a
          // form multi-select must show what is selected (parity with the
          // legacy MUI Chips), so rebuild the themed surface without the
          // pill override — same opt-in as FormSelect's multiple branch.
          styles={multiSelectSurfaceStyles}
          // Match selections by reference OR label (options re-created by a
          // refetch keep their labels; two options sharing a label resolve
          // to the first, unlike MUI's strict default), and drop values with
          // no matching option instead of mapping them to "-1": Mantine
          // echoes the value back through onChange, where options[-1] →
          // undefined would poison the form value (INS-1753).
          value={((currentValue as Option[]) || [])
            .filter((value: Option) => value != null)
            .map((value: Option) =>
              options.findIndex(
                (option) =>
                  option === value || getLabel(option) === getLabel(value),
              ),
            )
            .filter((index: number) => index >= 0)
            .map(keyOf)}
          onChange={(keys) => {
            const next = keys
              .map(optionAt)
              .filter((option): option is Option => option != null);
            field.handleChange(next);
            item.exposeValue?.(next);
          }}
        />
      ) : (
        <MantineSelect
          {...shared}
          value={(() => {
            if (currentValue == null || currentValue === "") return null;
            const index = options.findIndex(
              (option) =>
                option === currentValue ||
                getLabel(option) === getLabel(currentValue),
            );
            return index >= 0 ? keyOf(index) : null;
          })()}
          clearable={item.autoCompleteClearable ?? false}
          onChange={(key) => {
            if (key == null) {
              if (!item.autoCompleteClearable) return;
              field.handleChange("");
              item.exposeValue?.(null);
              item.autoCompleteExposeInputValue?.("");
              previousSearchRef.current = "";
              searchRef.current = "";
              setSearchValue("");
              return;
            }
            const next = optionAt(key);
            field.handleChange(next);
            item.exposeValue?.(next);
            // Clear the search directly (not via handleSearchChange, which
            // would overwrite the just-selected option on free-solo fields)
            // and keep the refs in sync so the blur restore stays inert.
            previousSearchRef.current = "";
            searchRef.current = "";
            setSearchValue("");
          }}
        />
      )}
    </FieldShell>
  );
};

export const FormCheckbox = ({ item, field, style }: FieldRendererProps) => (
  <FieldShell item={item} field={field} label={null} description={null}>
    <MantineCheckbox
      description={item.helperText}
      checked={
        field.state.value === undefined
          ? Boolean(item.defaultValue)
          : Boolean(field.state.value)
      }
      onChange={(e) => {
        field.handleChange(e.currentTarget.checked);
        item.exposeValue?.(e.currentTarget.checked);
      }}
      onBlur={field.handleBlur}
      label={item.label}
      disabled={item.disabled}
      size={item.size === "small" ? "sm" : "md"}
      error={hasError(field)}
      style={style}
    />
  </FieldShell>
);

export const FormRadioGroup = ({ item, field, style }: FieldRendererProps) => {
  const options: LabelValuePair[] = item.options ? [...item.options] : [];
  return (
    <FieldShell item={item} field={field}>
      <MantineRadio.Group
        value={String(field.state.value ?? item.defaultValue ?? "")}
        onChange={(value) => {
          field.handleChange(value);
          item.exposeValue?.(value);
        }}
        style={style}
      >
        <Stack gap={8} py={4}>
          {options.length > 0 ? (
            options.map((o) => (
              <MantineRadio
                key={String(o.value)}
                value={String(o.value)}
                label={o.label}
                disabled={o.disabled || item.disabled}
              />
            ))
          ) : (
            <Text size="sm" mx={16}>
              No options available.
            </Text>
          )}
        </Stack>
      </MantineRadio.Group>
    </FieldShell>
  );
};
